// src/pages/shared/NotificationsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { fetchNotifications as syncHeaderNotificationState } from '../../store/slices/notificationsSlice';
import { getNotifications, markAsRead, Notification } from '../../services/notificationService';
import { useUI } from '../../context/UIContext';
import { ArrowBackIcon, NotificationsIcon } from '../../components/common/Icons';
import '../../styles/NotificationsPage.css';
import { formatDistanceToNow } from 'date-fns';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const { setLoading: setUILoading, showSnackbar } = useUI();
  
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async (showLoadingIndicator = true) => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    if (showLoadingIndicator) {
      setLoading(true);
    }

    try {
      const response = await getNotifications({
        limit: 50,
        page: 1
      });

      if (response.success) {
        setNotifications(response.data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      showSnackbar('Failed to load notifications', 'error');
    } finally {
      setLoading(false);
    }
  }, [user?.id, showSnackbar]);

  useEffect(() => {
    fetchNotifications(true);
  }, [fetchNotifications]);


  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark as read if unread
      if (notification.status === 'unread') {
        try {
          await markAsRead([notification._id]);
          setNotifications(prev =>
            prev.map(n =>
              n._id === notification._id
                ? { ...n, status: 'read' }
                : n
            )
          );
          dispatch(syncHeaderNotificationState({ limit: 30, page: 1 }));
        } catch (error) {
          console.error('Error marking notification as read:', error);
        }
      }

      // Navigate based on notification type
      if (notification.type.includes('appointment_')) {
        let appointmentId: string | undefined;

        // Handle different appointment ID formats
        const data = notification.data as any;
        if (typeof data?.appointmentId === 'string') {
          appointmentId = data.appointmentId;
        } else if (data?.appointmentId?._id) {
          appointmentId = data.appointmentId._id;
        } else if (data?.appointmentDetails?._id) {
          appointmentId = data.appointmentDetails._id;
        }

        if (appointmentId) {
          // Determine user type from current route
          const userType = location.pathname.startsWith('/client') ? 'client' : 'stylist';
          navigate(`/${userType}/appointment/${appointmentId}`);
          return;
        }
      }

      // Handle salon invitation notifications
      if (notification.type.includes('salon_invitation_')) {
        // Navigate to invitations page if it exists
        const userType = location.pathname.startsWith('/client') ? 'client' : 'stylist';
        navigate(`/${userType}/invitations`);
        return;
      }

      // Handle deep links
      const data = notification.data as any;
      if (data?.action?.type === 'deep_link' && data.action.url) {
        // Navigate to the URL
        navigate(data.action.url);
      }
    } catch (error) {
      console.error('Error handling notification:', error);
      showSnackbar('Unable to process notification', 'error');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setUILoading(true);
      const unreadIds = notifications
        .filter(n => n.status === 'unread')
        .map(n => n._id);
      
      if (unreadIds.length > 0) {
        await markAsRead(unreadIds);
        setNotifications(prev =>
          prev.map(n => ({ ...n, status: 'read' }))
        );
        dispatch(syncHeaderNotificationState({ limit: 30, page: 1 }));
        showSnackbar('All notifications marked as read', 'success');
      }
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      showSnackbar('Failed to mark notifications as read', 'error');
    } finally {
      setUILoading(false);
    }
  };

  const getNotificationIcon = (type: string): string => {
    switch (type) {
      case 'appointment_reminder':
      case 'appointment_reminder_24h':
      case 'appointment_reminder_2h':
      case 'appointment_booked':
      case 'appointment_cancelled':
      case 'appointment_modified':
        return '📅';
      case 'payment_received':
      case 'payment_failed':
        return '💳';
      case 'review_received':
        return '⭐';
      case 'profile_update':
        return '👤';
      case 'service_update':
        return '✂️';
      case 'salon_invitation_received':
      case 'salon_invitation_accepted':
      case 'salon_invitation_declined':
        return '🏢';
      default:
        return '🔔';
    }
  };

  const hasUnread = notifications.some(n => n.status === 'unread');

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <button className="notifications-back" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <h1 className="notifications-title">Notifications</h1>
        {hasUnread && (
          <button
            className="notifications-mark-all"
            onClick={handleMarkAllRead}
          >
            Mark all as read
          </button>
        )}
      </div>

      <div className="notifications-content">
        {loading ? (
          <div className="notifications-loading">
            <div className="tesla-loader"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">
            <div className="notifications-empty__icon">
              <NotificationsIcon size={64} />
            </div>
            <h3 className="notifications-empty__title">No notifications yet</h3>
            <p className="notifications-empty__text">
              You'll see notifications about appointments, messages, and updates here.
            </p>
          </div>
        ) : (
          <div className="notifications-list">
            {notifications.map((notification) => {
              const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
                addSuffix: true
              });

              return (
                <div
                  key={notification._id}
                  className={`notification-item ${notification.status === 'unread' ? 'notification-item--unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-item__icon">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="notification-item__content">
                    <div className="notification-item__title">
                      {notification.title}
                    </div>
                    <div className="notification-item__message">
                      {notification.message}
                    </div>
                    <div className="notification-item__time">
                      {timeAgo}
                    </div>
                  </div>
                  {notification.status === 'unread' && (
                    <div className="notification-item__badge"></div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;

